import { useContext, useEffect } from 'react';

import {
  GaugeChartV2,
  type GaugeChartV2Entity,
  type GaugeChartV2TooltipRender,
} from '@revelio/replots';

import type { MultiMetricDataMap } from '../DataProvider/MultiMetricProvider';
import type { SingleDataSet } from '../DataProvider/types';
import { ChartError } from './ChartError';
import type { ChartExportInfo } from './chart-menu/chart-export-types';
import { PageExportContext } from './chart-menu/PageExportProvider';
import { GaugeExportMeta } from './GaugeExportMeta';
import {
  useProcessContent,
  useProcessContentByEntity,
} from './TextChart/utils/use-process-content';
import { useHoldExportWhilePending } from './use-hold-export-while-pending';
import { buildGaugeV2Tooltip } from './utils/build-gauge-v2-tooltip';
import type { ParameterSelection } from './utils/check-datapoint-in-parameter';

const GAUGE_V2_EXPORT_FIELDS: (keyof ChartExportInfo)[] = [
  'gaugeMainText',
  'gaugeValue',
  'gaugeEntities',
  'gaugeColors',
  'gaugeMinLabel',
  'gaugeMaxLabel',
];

type GaugeV2Props = {
  valueTemplate: string;
  minLabel?: string;
  maxLabel?: string;
  minTemplate?: string;
  maxTemplate?: string;
  multiData?: MultiMetricDataMap;
  colorPalette?: string[];
  chartId?: string;
  primarySelections?: ParameterSelection[];
};

const isPrimaryEntity = (
  { dim, id }: { dim: string; id?: string | null },
  primarySelections: ParameterSelection[]
) =>
  primarySelections.some(
    (selection) =>
      (id != null && String(selection.id) === String(id)) ||
      selection.name === dim
  );

const stripHtml = (html: string | null): string => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').trim();
};

export const GaugeV2 = (props: GaugeV2Props) => {
  const { valueTemplate, multiData } = props;
  const trimmedValue = valueTemplate.trim();
  const isDirectNumber =
    trimmedValue.length > 0 && !Number.isNaN(Number(trimmedValue));

  const { processedContent: processedValueContent, error: valueError } =
    useProcessContentByEntity({
      content: isDirectNumber ? '' : valueTemplate,
      multiData,
    });

  const directValue = isDirectNumber ? Number(trimmedValue) : null;
  const allEntityValues: SingleDataSet['data'] =
    processedValueContent?.map(({ entity, id, processedContent }) => {
      const parsed = Number(stripHtml(processedContent));
      return {
        dim: entity,
        id: id ?? null,
        value:
          directValue ??
          (processedContent.length > 0 && Number.isFinite(parsed)
            ? parsed
            : null),
      };
    }) ?? [];
  const primaryEntityValue = props.primarySelections?.length
    ? allEntityValues.find((entry) =>
        isPrimaryEntity(entry, props.primarySelections ?? [])
      )
    : undefined;
  const entityValues = primaryEntityValue
    ? [primaryEntityValue]
    : allEntityValues;

  const entities: GaugeChartV2Entity[] =
    directValue !== null
      ? [{ value: directValue }]
      : entityValues.flatMap(({ dim, id, value }) =>
          value === null
            ? []
            : [
                {
                  value,
                  ...(id && { id }),
                  ...(entityValues.length > 1 && { label: dim }),
                },
              ]
        );

  const tooltipRender = buildGaugeV2Tooltip({
    valueTemplate,
    multiData,
    entityValues,
  });

  const isTemplateStillProcessing =
    !isDirectNumber &&
    trimmedValue.length > 0 &&
    processedValueContent === null &&
    !valueError;

  useHoldExportWhilePending(props.chartId, 'value', isTemplateStillProcessing);

  return (
    <InternalGaugeV2
      entities={entities}
      processing={isTemplateStillProcessing}
      tooltipRender={tooltipRender}
      {...props}
    />
  );
};

const InternalGaugeV2 = ({
  entities,
  processing,
  minLabel,
  maxLabel,
  minTemplate,
  maxTemplate,
  multiData,
  colorPalette,
  chartId,
  tooltipRender,
}: Omit<GaugeV2Props, 'valueTemplate'> & {
  entities: GaugeChartV2Entity[];
  processing: boolean;
  tooltipRender?: GaugeChartV2TooltipRender;
}) => {
  const { processedContent: processedMin } = useProcessContent({
    content: minTemplate ?? '',
    multiData,
  });

  const { processedContent: processedMax } = useProcessContent({
    content: maxTemplate ?? '',
    multiData,
  });

  const minValue = (() => {
    if (!minTemplate) return 0;
    const direct = Number(minTemplate.trim());
    if (!Number.isNaN(direct)) return direct;
    const parsed = Number(stripHtml(processedMin));
    return Number.isFinite(parsed) ? parsed : 0;
  })();

  const maxValue = (() => {
    if (!maxTemplate) return 1;
    const direct = Number(maxTemplate.trim());
    if (!Number.isNaN(direct)) return direct;
    const parsed = Number(stripHtml(processedMax));
    return Number.isFinite(parsed) ? parsed : 1;
  })();

  const clampedEntities = entities.map((entity) => ({
    ...entity,
    value: Math.max(minValue, Math.min(maxValue, entity.value)),
  }));

  const hasNoData = clampedEntities.length === 0 && !processing;

  const gaugeColors =
    colorPalette && colorPalette.length >= 2 ? colorPalette : undefined;

  const pageExport = useContext(PageExportContext);
  useEffect(() => {
    if (!chartId || !pageExport || !hasNoData) return;
    pageExport.clearChartExportInfo(chartId, GAUGE_V2_EXPORT_FIELDS);
  }, [chartId, pageExport, hasNoData]);

  if (hasNoData) {
    return <ChartError />;
  }

  const domain = maxValue - minValue;
  const toRatio = (value: number) =>
    domain === 0 ? 0 : (value - minValue) / domain;
  const exportEntities = clampedEntities.map(({ label, value }) => ({
    label,
    value: toRatio(value),
  }));
  const [firstEntity] = exportEntities;
  const valueLabel = firstEntity
    ? `${Math.round(firstEntity.value * 100)}%`
    : '';

  return (
    <>
      {chartId && (
        <GaugeExportMeta
          chartId={chartId}
          enabled={!hasNoData}
          gaugeMainText={valueLabel}
          gaugeValue={firstEntity?.value ?? 0}
          gaugeEntities={exportEntities}
          gaugeColors={gaugeColors}
          gaugeMinLabel={minLabel}
          gaugeMaxLabel={maxLabel}
        />
      )}
      <GaugeChartV2
        entities={clampedEntities}
        minValue={minValue}
        maxValue={maxValue}
        minLabel={minLabel}
        maxLabel={maxLabel}
        colorRange={gaugeColors}
        tooltipRender={tooltipRender}
      />
    </>
  );
};
